import { PlatformLocation } from '@angular/common';
import { Component, Inject, Optional } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA} from '@angular/material/legacy-dialog';
import { filter, takeUntil } from 'rxjs/operators';
import { BaseComponent } from '../../shared/base-components/base.component';
import { CustomerSearchViewContext, CustomerSearchViewModalComponent } from '../../shared/components/customer-searchbar/search-modal.component';
import { getCurrentDateToString, getServerDate, isStartAfterEnd, setServerDateToString } from '../../shared/helpers/time-helpers.model';
import { MvdModalService } from '../../shared/services/modal/mvd-modal.service';
import { CustomerAffiliationMember } from '../models/customer-affiliation-member.model';
import { CustomerAffiliationRoleType } from '../models/customer-affiliation-role-type.model';
import { CustomerAffiliation } from '../models/customer-affiliation.model';
import { CustomerAffiliationType, ICustomerAffiliationType } from '../models/enums/customer-affiliation-type.enum';
import { CustomerCategoryType } from '../models/enums/customer-category-type.enum';
import { CustomerManageService } from '../shared/customer-manage.service';

export class AffiliationContext {
    constructor(public affiliationId: number, public customerNumberId: number) {}
}

@Component({
    selector: 'customer-affiliation-edit',
    templateUrl: './customer-affiliation-edit.html'
})

export class CustomerAffiliationEditComponent extends BaseComponent {
    public affiliationId: number;
    public customerNumber: number;
    public currentCustomerName: string;
    public showButtonLoading = false;
    public roleTypesLoading = true;
    public showPrimaryValidationError = false;
    public affiliation: CustomerAffiliation;
    public primary: CustomerAffiliationMember;
    public affiliates: CustomerAffiliationMember[];

    public affiliationForm: UntypedFormGroup;

    public primaryAffiliationRoleType: string;

    public customerAffiliationTypes: ICustomerAffiliationType[] = (new CustomerAffiliationType).GetActive();
    public customerAffiliationRoleTypes: CustomerAffiliationRoleType[][] = [];
    private affiliationRoleTypes: CustomerAffiliationRoleType[][] = [];

    constructor(
        public location: PlatformLocation,
        public fb: UntypedFormBuilder,
        public service: CustomerManageService,
        public dialogRef: MatDialogRef<CustomerAffiliationEditComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: AffiliationContext,
        public modalService: MvdModalService,
    ) {
        super();
        this.affiliationId = this.data.affiliationId;
        this.customerNumber = this.data.customerNumberId;

        location.onPopState(() => {
            this.dialogRef.close();
        });
    }

    ngOnInit() {
        this.affiliationForm = this.fb.group({
            'affiliationTypeId': [null],
            'effectiveDate': [getCurrentDateToString(), Validators.required],
            'endDate': [null],
            'customerAffiliationRoleTypeIds': this.fb.array([]),
        }, {
            validator: this.startDateValidator
        });

        this.affiliationForm.get('affiliationTypeId').valueChanges
            .pipe(takeUntil(this.componentDestroyed))
            .subscribe(id => this.onAffiliationTypeChange(id));

        this.affiliation = new CustomerAffiliation();
        this.primary = new CustomerAffiliationMember();

        if (this.affiliationId != 0) {
            this.loadAffiliation();
        } else {
            this.initAffiliation();
        }
    }

    private addCustomerAffiliationRoleTypeIdControl(customerAffiliationRoleTypeId: number) {
        const control = this.fb.control(customerAffiliationRoleTypeId, Validators.required);
        const customerAffiliationRoleTypeIds = this.affiliationForm.get('customerAffiliationRoleTypeIds') as UntypedFormArray;
        customerAffiliationRoleTypeIds.push(control);
    }

    private initAffiliation() {
        this.affiliation = new CustomerAffiliation();
        this.affiliation.customerNumberID = this.customerNumber;
        this.affiliation.members = [];
        this.addAffiliationMember(this.customerNumber, true);
    }

    private loadAffiliation() {
        this.service.getCustomerAffiliation(this.affiliationId)
            .subscribe(results => {
                this.affiliation = results;
                const effDate = getServerDate(this.affiliation, 'effectiveDate');
                this.affiliationForm.get('effectiveDate').setValue(effDate);
                const endDate = getServerDate(this.affiliation, 'endDate');
                this.affiliationForm.get('endDate').setValue(endDate);
                this.affiliates = this.affiliation.members;
                this.affiliates.forEach(a => this.service.getCustomerType(a.customerNumberID).subscribe(r => a.customerTypeId = r));
                this.getAffiliationTypes(this.affiliation.customerAffiliationTypeId);
                const affiliationTypeIdControl = this.affiliationForm.get('affiliationTypeId');
                affiliationTypeIdControl.setValue(this.affiliation.customerAffiliationTypeId);
                this.affiliates.map(affiliate => {
                    this.addCustomerAffiliationRoleTypeIdControl(affiliate.customerAffiliationRoleTypeId);
                });
            },
            e => this.handleValidationResult(e)
            );
    }

    private onAffiliationTypeChange(value) {
        this.getAffiliationTypes(value);
    }

    private getAffiliationTypes(affiliationType: number) {
        let cats = new CustomerCategoryType();
        if (!this.affiliationRoleTypes[affiliationType]) {
            this.roleTypesLoading = true;
            this.service.getCustomerAffiliationRoleTypes(affiliationType)
                .subscribe(
                    result => {
                        this.affiliationRoleTypes[affiliationType] = result;
                        this.setCustomerAffiliationRoleTypes(this.affiliationRoleTypes[affiliationType]);
                        this.roleTypesLoading = false;
                    },
                    e => console.log('error', e)
                );
        } else {
            this.setCustomerAffiliationRoleTypes(this.affiliationRoleTypes[affiliationType]);
        }
    }

    public onRoleTypeChange(customerAffiliationRoleTypeId: number, index: number) {
        const isPrimary = this.customerAffiliationRoleTypes[0].find(rt => rt.id == customerAffiliationRoleTypeId).isPrimary;
        const affiliate = this.affiliates[index];
        affiliate.isPrimary = isPrimary;
        affiliate.customerAffiliationRoleTypeId = customerAffiliationRoleTypeId;
    }

    private setCustomerAffiliationRoleTypes(array: CustomerAffiliationRoleType[]) {
        let cats = new CustomerCategoryType();
        this.customerAffiliationRoleTypes[0] = array;
        this.customerAffiliationRoleTypes[cats.Individual.id] = this.customerAffiliationRoleTypes[0].filter(rt => rt.customerCategoryTypeID != cats.Organization.id);
        this.customerAffiliationRoleTypes[cats.Organization.id] = this.customerAffiliationRoleTypes[0].filter(rt => rt.customerCategoryTypeID != cats.Individual.id);
        this.primaryAffiliationRoleType = this.customerAffiliationRoleTypes[0].find(rt => rt.isPrimary).customerAffiliationRoleTypeDisplayValue;
    }

    private createName(customerName: any): string {
        let cName = '';
        if (customerName.organizationName != null) {
            cName = customerName.organizationName;
        } else {
            let middleName: string = !customerName.middleName ? '' : customerName.middleName + ' ';
            cName = `${customerName.firstName} ${middleName} ${customerName.lastName}`;
        }
        return cName;
    }

    private addAffiliationMember(customerNumber: number, isPrimary: boolean = false) {
        const customer = new CustomerAffiliationMember();
        customer.customerNumberID = customerNumber;
        customer.isPrimary = isPrimary;
        customer.effectiveDate = getCurrentDateToString();
        this.service.getCustomerLegalName(customerNumber).subscribe(
            name => {
                this.service.getCustomerType(customerNumber).subscribe(
                    type => {
                        customer.customerName = this.createName(name);
                        customer.customerTypeId = type;
                        this.affiliation.members.push(customer);
                        this.affiliates = this.affiliation.members;
                        this.addCustomerAffiliationRoleTypeIdControl(customer.customerAffiliationRoleTypeId);
                    },
                    e => this.handleValidationResult(e)
                );
            },
            e => this.handleValidationResult(e)
        );
    }

    public cancel() {
        this.dialogRef.close();
    }

    public addAffiliate() {
        const data = new CustomerSearchViewContext(undefined);
        const dialogRef = this.modalService.open(CustomerSearchViewModalComponent, data);
        dialogRef.afterClosed()
            .subscribe(
                result => {
                    if (result && result > 0) {
                        this.addAffiliationMember(result);
                    }
                },
                err => {}
            );
    }

    private getModel(): CustomerAffiliation {
        const model = this.affiliation;
        model.customerAffiliationTypeId = +this.affiliationForm.get('affiliationTypeId').value;
        model.effectiveDate = setServerDateToString(this.affiliationForm.get('effectiveDate').value);
        model.endDate = setServerDateToString(this.affiliationForm.get('endDate').value);

        const customerAffiliationRoleTypeIds = this.affiliationForm.get('customerAffiliationRoleTypeIds') as UntypedFormArray;
        model.members.forEach((member, index) => {
            member.customerAffiliationRoleTypeId = +customerAffiliationRoleTypeIds.controls[index].value;
        });

        return model;
    }

    public saveAffiliation() {
        this.showButtonLoading = true;
        this.showPrimaryValidationError = false;

        const model = this.getModel();

        if (!this.validateMembers(model)) {
            this.showButtonLoading = false;
            this.showPrimaryValidationError = true;
            return;
        }

        this.service.saveCustomerAffiliation(model)
            .subscribe(result => {
            },
            e => this.handleValidationResult(e),
            () => {
                this.showButtonLoading = false;
                this.dialogRef.close(true);
            });
    }

    public deleteAffiliate(index) {
        const selectedAffiliate = this.affiliates[index];

        this.modalService.confirm()
            .title('Warning')
            .body('Are you sure you want to permanently delete ' + selectedAffiliate.customerName + ' from ' + this.affiliation.customerAffiliationTypeDisplayValue + '?')
            .okBtn('Confirm Delete').cancelBtn('Cancel')
            .open().afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(() => {
                const oldAffiliate = this.affiliates.splice(index, 1);
                const customerAffiliationRoleTypeIds = this.affiliationForm.get('customerAffiliationRoleTypeIds') as UntypedFormArray;
                customerAffiliationRoleTypeIds.removeAt(index);

                const affiliateIndex = this.affiliation.members.indexOf(oldAffiliate[0]);

                if (affiliateIndex > -1) {
                    if (this.affiliation.members[affiliateIndex].id && this.affiliation.members[affiliateIndex].id != 0) {
                        this.affiliation.members[affiliateIndex].endDate = getCurrentDateToString();
                    } else {
                        this.affiliation.members.splice(affiliateIndex, 1);
                    }
                }
            });
    }

    private validateMembers(model: CustomerAffiliation): boolean {
        let count = 0;
        for (const member of model.members) {
            const roleType = this.customerAffiliationRoleTypes[0].find(rt => rt.id == member.customerAffiliationRoleTypeId);
            if (roleType && roleType.isPrimary) {
                count++;
            }
        }

        return count == 1;
    }

    private startDateValidator(group: UntypedFormGroup): { [key: string]: boolean } {
        const effDate = group.get('effectiveDate').value;
        const endDate = group.get('endDate').value;
        if ((effDate != null && endDate != null)) {
            if (isStartAfterEnd(effDate, endDate)) {
                return { 'startAfterEndDate': true };
            }
        }
    }
}
